import { type ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "@/shared/lib/cn";

export interface TooltipProps {
  /** Text shown in the bubble. Mirror the trigger's `aria-label`. */
  label: string;
  /** The trigger element(s) the tooltip describes. */
  children: ReactNode;
  side?: "top" | "bottom";
  /** Hover/focus dwell before the bubble appears (DESIGN §5). */
  delayMs?: number;
  className?: string;
}

/**
 * A lightweight, dependency-free label tooltip: reveals after a short dwell on
 * hover or focus and hides immediately on leave/blur. Purely supplemental — the
 * trigger must still carry its own `aria-label`; the bubble is `aria-hidden`.
 */
export function Tooltip({ label, children, side = "top", delayMs = 400, className }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  useEffect(() => clear, []);

  const show = () => {
    clear();
    timer.current = setTimeout(() => setOpen(true), delayMs);
  };
  const hide = () => {
    clear();
    setOpen(false);
  };

  return (
    <span
      className={cn("relative inline-flex", className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocusCapture={show}
      onBlurCapture={hide}
    >
      {children}
      <span
        role="tooltip"
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute left-1/2 z-50 -translate-x-1/2 whitespace-nowrap rounded-md bg-ink px-2 py-1 font-sans text-[11px] font-medium text-paper shadow-[var(--shadow-pill)] transition-opacity duration-150 ease-house",
          side === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5",
          open ? "opacity-100" : "opacity-0",
        )}
      >
        {label}
      </span>
    </span>
  );
}
